import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  eventStatuses,
  type DepartmentView,
  type EventView,
  type PageResponse,
  type SeriesGenerationView,
  type SupportedLocale,
  type VenueView,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { AttendanceSummary } from './AttendanceSummary';
import { formatCalendarDate } from './athletes';
import { EventDetail } from './EventDetail';
import { EventForm } from './EventForm';
import {
  addCalendarDays,
  eventStatusKeys,
  eventTypeKeys,
  eventsPath,
  groupByDate,
  localDayOf,
} from './scheduling';
import { TaskSection } from './TaskSection';

export interface StaffCalendarProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}` */
  base: string;
  csrfToken: string;
  canManage: boolean;
  canRecord: boolean;
  canAttachPlan: boolean;
  /** An event named by the address, opened as soon as the page loads. */
  initialEventId?: string | undefined;
}

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

const defaultRangeDays = 14;

/** The club calendar for staff: events by day for a chosen period, with the form to add more. */
export function StaffCalendar({
  locale,
  base,
  csrfToken,
  canManage,
  canRecord,
  canAttachPlan,
  initialEventId,
}: StaffCalendarProps): React.JSX.Element {
  const today = localDayOf(new Date());
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(addCalendarDays(today, defaultRangeDays));
  const [departmentId, setDepartmentId] = useState('');
  const [status, setStatus] = useState('');
  const [departments, setDepartments] = useState<DepartmentView[]>([]);
  const [venues, setVenues] = useState<VenueView[]>([]);
  const [events, setEvents] = useState<EventView[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [openId, setOpenId] = useState<string | null>(initialEventId ?? null);
  const [notice, setNotice] = useState<SeriesGenerationView | 'event' | null>(null);

  useEffect(() => {
    apiRequest<DepartmentView[]>(`${base}/departments`).then(setDepartments, () => {
      setDepartments([]);
    });
    apiRequest<VenueView[]>(`${base}/venues`).then(setVenues, () => {
      setVenues([]);
    });
  }, [base]);

  const load = useCallback(async () => {
    setErrorKey(null);
    try {
      const page = await apiRequest<PageResponse<EventView>>(
        eventsPath(base, { fromDate, toDate, departmentId, status }),
      );
      setEvents(page.items);
      setNextCursor(page.nextCursor);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base, fromDate, toDate, departmentId, status]);

  useEffect(() => {
    void load();
  }, [load]);

  async function loadMore(): Promise<void> {
    if (nextCursor === null) return;
    try {
      const page = await apiRequest<PageResponse<EventView>>(
        eventsPath(base, { fromDate, toDate, departmentId, status, cursor: nextCursor }),
      );
      setEvents((current) => [...(current ?? []), ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  const departmentName = (identifier: string): string =>
    departments.find((department) => department.id === identifier)?.name ?? '';

  return (
    <Box component="section" aria-label={translate(locale, 'calendar.staff.title')}>
      <Typography variant="h5" component="h2" gutterBottom>
        {translate(locale, 'calendar.staff.title')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          size="small"
          type="date"
          label={translate(locale, 'calendar.filter.from')}
          value={fromDate}
          onChange={(event) => {
            setFromDate(event.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          size="small"
          type="date"
          label={translate(locale, 'calendar.filter.to')}
          value={toDate}
          onChange={(event) => {
            setToDate(event.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        {departments.length > 1 && (
          <TextField
            select
            size="small"
            sx={{ minWidth: 160 }}
            label={translate(locale, 'calendar.filter.department')}
            value={departmentId}
            onChange={(event) => {
              setDepartmentId(event.target.value);
            }}
          >
            <MenuItem value="">{translate(locale, 'calendar.filter.all')}</MenuItem>
            {departments.map((department) => (
              <MenuItem key={department.id} value={department.id}>
                {department.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          size="small"
          sx={{ minWidth: 150 }}
          label={translate(locale, 'calendar.filter.status')}
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
          }}
        >
          <MenuItem value="">{translate(locale, 'calendar.filter.all')}</MenuItem>
          {eventStatuses.map((candidate) => (
            <MenuItem key={candidate} value={candidate}>
              {translate(locale, eventStatusKeys[candidate])}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {notice === 'event' && (
        <Alert
          severity="success"
          sx={{ mb: 1 }}
          onClose={() => {
            setNotice(null);
          }}
        >
          {translate(locale, 'calendar.create.created')}
        </Alert>
      )}
      {notice !== null && notice !== 'event' && (
        <Alert
          severity="success"
          sx={{ mb: 1 }}
          onClose={() => {
            setNotice(null);
          }}
        >
          {translate(locale, 'calendar.create.seriesResult')} {notice.created}
          {notice.conflicts.length > 0 &&
            ` · ${translate(locale, 'calendar.create.seriesConflicts')} ${String(notice.conflicts.length)}`}
          {notice.unpublished > 0 &&
            ` · ${translate(locale, 'calendar.create.seriesUnpublished')} ${String(notice.unpublished)}`}
        </Alert>
      )}
      {events === null && errorKey === null && (
        <Typography>{translate(locale, 'calendar.loading')}</Typography>
      )}
      {events?.length === 0 && (
        <Typography color="text.secondary">{translate(locale, 'calendar.empty')}</Typography>
      )}
      {events !== null &&
        groupByDate(events).map((group) => (
          <Box key={group.date} sx={{ mb: 2 }}>
            <Typography variant="h6" component="h3">
              {formatCalendarDate(group.date, locale)}
            </Typography>
            <Stack spacing={1}>
              {group.items.map((event) => (
                <Box key={event.id}>
                  <Paper variant="outlined" sx={{ padding: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 600 }}>
                        {event.startTime}–{event.endTime}
                      </Typography>
                      <Typography>{event.title}</Typography>
                      <Chip
                        size="small"
                        label={translate(locale, eventTypeKeys[event.eventType])}
                      />
                      <Chip
                        size="small"
                        color={
                          event.status === 'published'
                            ? 'success'
                            : event.status === 'cancelled'
                              ? 'error'
                              : 'default'
                        }
                        label={translate(locale, eventStatusKeys[event.status])}
                      />
                      {departments.length > 1 && (
                        <Typography variant="body2" color="text.secondary">
                          {departmentName(event.departmentId)}
                        </Typography>
                      )}
                      {event.resourceName !== null && (
                        <Typography variant="body2" color="text.secondary">
                          {event.resourceName}
                        </Typography>
                      )}
                      <Button
                        size="small"
                        sx={{ ml: 'auto', minHeight: 44 }}
                        aria-expanded={openId === event.id}
                        onClick={() => {
                          setOpenId(openId === event.id ? null : event.id);
                        }}
                      >
                        {translate(
                          locale,
                          openId === event.id ? 'calendar.detail.close' : 'calendar.detail.open',
                        )}
                      </Button>
                    </Box>
                  </Paper>
                  {openId === event.id && (
                    <EventDetail
                      locale={locale}
                      base={base}
                      csrfToken={csrfToken}
                      eventId={event.id}
                      canManage={canManage}
                      canRecord={canRecord}
                      canAttachPlan={canAttachPlan}
                      onChanged={() => {
                        void load();
                      }}
                      onClose={() => {
                        setOpenId(null);
                      }}
                    />
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
      {openId !== null && events?.every((event) => event.id !== openId) === true && (
        <EventDetail
          locale={locale}
          base={base}
          csrfToken={csrfToken}
          eventId={openId}
          canManage={canManage}
          canRecord={canRecord}
          canAttachPlan={canAttachPlan}
          onChanged={() => {
            void load();
          }}
          onClose={() => {
            setOpenId(null);
          }}
        />
      )}
      {nextCursor !== null && (
        <Button
          sx={{ minHeight: 44 }}
          onClick={() => {
            void loadMore();
          }}
        >
          {translate(locale, 'calendar.loadMore')}
        </Button>
      )}

      {canManage && departments.length > 0 && (
        <TaskSection locale={locale} titleKey="calendar.create.title">
          <EventForm
            locale={locale}
            base={base}
            csrfToken={csrfToken}
            departments={departments}
            venues={venues}
            defaultDate={today}
            onCreated={(created) => {
              setNotice(created.series ?? 'event');
              if (created.eventId !== null) setOpenId(created.eventId);
              void load();
            }}
          />
        </TaskSection>
      )}
      <TaskSection locale={locale} titleKey="calendar.summary.title">
        <AttendanceSummary
          key={`${base}:${fromDate}:${toDate}:${departmentId}`}
          locale={locale}
          base={base}
          fromDate={fromDate}
          toDate={toDate}
          departmentId={departmentId}
        />
      </TaskSection>
    </Box>
  );
}
